import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export type CustomerSubuserStatus = 'INVITED' | 'ACTIVE' | 'DISABLED';
export type CustomerSubuserRole = 'PRIMARY' | 'SUBUSER';

export interface CustomerSubuserUser {
  firebaseUid: string;
  email: string;
  name: string | null;
  phone?: string | null;
}

export interface CustomerSubuser {
  id: string;
  customerId: string;
  userId: string | null;
  email: string;
  name: string | null;
  role: CustomerSubuserRole;
  status: CustomerSubuserStatus;
  inviteExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: CustomerSubuserUser | null;
  enabledAddressCount?: number;
}

export interface ListCustomerSubusersResponse {
  page?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
  subusers: CustomerSubuser[];
}

export interface InviteCustomerSubuserPayload {
  email: string;
  name: string;
}

export interface InviteCustomerSubuserResponse {
  customerUserId: string;
  inviteUrl?: string;
  inviteExpiresAt: string;
}

export interface UpdateCustomerSubuserStatusPayload {
  customerUserId: string;
  status: Extract<CustomerSubuserStatus, 'ACTIVE' | 'DISABLED'>;
}

export interface UpdateCustomerSubuserStatusResponse {
  subuser: CustomerSubuser;
}

export interface CustomerSubuserAddressAccessItem {
  id: string;
  label: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  isEnabled: boolean;
}

export interface CustomerSubuserAddressAccessResponse {
  subuser: {
    id: string;
    email: string;
    name: string | null;
    status: CustomerSubuserStatus;
  };
  addresses: CustomerSubuserAddressAccessItem[];
}

export interface UpdateCustomerSubuserAddressAccessPayload {
  addresses: {
    deliveryAddressId: string;
    isEnabled: boolean;
  }[];
}

export interface UpdateCustomerSubuserAddressAccessResponse {
  success: boolean;
}

export interface CustomerSubuserInviteInfoResponse {
  email: string;
  name: string | null;
  customerName: string | null;
  orgName: string | null;
  inviteExpiresAt: string | null;
  status: CustomerSubuserStatus;
}

export interface AcceptCustomerSubuserInviteResponse {
  success: boolean;
  customerUser: CustomerSubuser;
}

export function useListCustomerSubusers(enabled = true) {
  return useQuery<CustomerSubuser[]>({
    queryKey: ['customer-subusers'],
    queryFn: async () => {
      const { data } = await api.get<ListCustomerSubusersResponse>('/customer-subusers');
      return data?.subusers || [];
    },
    enabled,
  });
}

export function useInviteCustomerSubuser() {
  const qc = useQueryClient();

  return useMutation<InviteCustomerSubuserResponse, any, InviteCustomerSubuserPayload>({
    mutationFn: async (payload) => {
      const { data } = await api.post<InviteCustomerSubuserResponse>(
        '/customer-subusers/invite',
        payload
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-subusers'] });
    },
  });
}

export function useUpdateCustomerSubuserStatus() {
  const qc = useQueryClient();

  return useMutation<UpdateCustomerSubuserStatusResponse, any, UpdateCustomerSubuserStatusPayload>({
    mutationFn: async ({ customerUserId, status }) => {
      const { data } = await api.patch<UpdateCustomerSubuserStatusResponse>(
        `/customer-subusers/${customerUserId}`,
        { status }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-subusers'] });
    },
  });
}

export function useCustomerSubuserAddressAccess(
  customerUserId: string | null | undefined,
  enabled = true
) {
  return useQuery<CustomerSubuserAddressAccessResponse>({
    queryKey: ['customer-subusers', customerUserId, 'address-access'],
    queryFn: async () => {
      const { data } = await api.get<CustomerSubuserAddressAccessResponse>(
        `/customer-subusers/${customerUserId}/address-access`
      );
      return data;
    },
    enabled: !!customerUserId && enabled,
  });
}

export function useUpdateCustomerSubuserAddressAccess(customerUserId: string) {
  const qc = useQueryClient();

  return useMutation<
    UpdateCustomerSubuserAddressAccessResponse,
    any,
    UpdateCustomerSubuserAddressAccessPayload
  >({
    mutationFn: async (payload) => {
      const { data } = await api.put<UpdateCustomerSubuserAddressAccessResponse>(
        `/customer-subusers/${customerUserId}/address-access`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-subusers'] });
      qc.invalidateQueries({
        queryKey: ['customer-subusers', customerUserId, 'address-access'],
      });
    },
  });
}

export function useCustomerSubuserInviteInfo(token: string, enabled = true) {
  return useQuery<CustomerSubuserInviteInfoResponse>({
    queryKey: ['customer-subuser-invite', token],
    queryFn: async () => {
      const { data } = await api.get<CustomerSubuserInviteInfoResponse>(
        `/customer-subusers/invite-info/${token}`
      );
      return data;
    },
    enabled: !!token && enabled,
    retry: false,
  });
}

export function useAcceptCustomerSubuserInvite(token: string | undefined) {
  const qc = useQueryClient();

  return useMutation<AcceptCustomerSubuserInviteResponse, any, void>({
    mutationFn: async () => {
      const { data } = await api.post<AcceptCustomerSubuserInviteResponse>(
        '/customer-subusers/accept-invite',
        { token }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['current-user'] });
      qc.invalidateQueries({ queryKey: ['customer-subusers'] });
    },
  });
}
